import { RefObject, useCallback, useEffect, useRef, useState } from "react";

export function useScrollContainer<T extends HTMLElement>(
  ref: RefObject<T>,
  direction: "horizontal" | "vertical"
) {
  const [canScrollBackward, setCanScrollBackward] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(true);
  const [scrollSpeed, setScrollSpeed] = useState<number>(0);
  const interval = useRef<NodeJS.Timeout | null>(null);

  // Scroll container continuously while scrollSpeed !== 0
  const element = ref.current;
  useEffect(() => {
    if (interval.current) clearInterval(interval.current);
    interval.current = setInterval(() => {
      if (direction === "horizontal") {
        element?.scrollBy(scrollSpeed, 0);
      } else {
        element?.scrollBy(0, scrollSpeed);
      }
    }, 10);
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [direction, scrollSpeed, element]);

  const stopScrolling = useCallback(() => setScrollSpeed(0), []);

  const scrollListener = useCallback(() => {
    const currentTableContainerRef = ref.current;
    setCanScrollBackward(
      direction === "horizontal"
        ? !!currentTableContainerRef?.scrollLeft
        : !!currentTableContainerRef?.scrollTop
    );
    setCanScrollForward(
      direction === "horizontal"
        ? (currentTableContainerRef?.scrollWidth ?? 0) -
            ((currentTableContainerRef?.scrollLeft ?? 0) +
              (currentTableContainerRef?.getBoundingClientRect().width ?? 0)) >
            1
        : (currentTableContainerRef?.scrollHeight ?? 0) -
            ((currentTableContainerRef?.scrollTop ?? 0) +
              (currentTableContainerRef?.getBoundingClientRect().height ?? 0)) >
            1
    );
  }, [ref, direction]);

  // check if element can scroll on window resize
  useEffect(() => {
    window.addEventListener("resize", scrollListener);
    return () => {
      window.removeEventListener("resize", scrollListener);
    };
  }, [scrollListener]);

  // check if element can scroll after scrolling it
  useEffect(() => {
    const currentTableContainerRef = ref.current;
    currentTableContainerRef?.addEventListener("scroll", scrollListener);
    return () => {
      currentTableContainerRef?.removeEventListener("scroll", scrollListener);
    };
  }, [ref, direction, scrollListener]);

  // check if element can scroll at the very beginning (right after mount)
  const hasRunListerOnce = useRef(false);
  useEffect(() => {
    if (!hasRunListerOnce.current) {
      scrollListener();
      hasRunListerOnce.current = true;
    }
  }, [scrollListener]);

  return {
    canScroll: canScrollBackward || canScrollForward,
    canScrollBackward,
    canScrollForward,
    startScrolling: setScrollSpeed,
    stopScrolling,
    isScrolling: scrollSpeed !== 0,
  };
}
